import React, { useState } from 'react';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, Lock as LockIcon } from '@mui/icons-material';
import { VERSION_INFO } from '../constants/version';
import { getApiUrl } from '../utils/httpClient';

interface LoginProps {
  onLogin: (token: string) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [passcode, setPasscode] = useState('');
  const [showPasscode, setShowPasscode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(getApiUrl('/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ passcode }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        localStorage.setItem('auth_token', data.token);
        onLogin(data.token);
      } else {
        setError('Invalid passcode. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(`Authentication failed. Please check your connection and try again. (Trying: ${getApiUrl('/auth/login')})`);
    } finally {
      setLoading(false);
    }
  };

  const toggleShowPasscode = () => {
    setShowPasscode(!showPasscode);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.50',
        px: 2,
      }}
    >
      <Card
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        }}
      >
        {/* Logo/Brand */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1976d2 0%, #4c2a85 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.5rem' }}>
              AC
            </Typography>
          </Box>
          <Typography variant="h5" fontWeight={700} color="text.primary" gutterBottom>
            Alfred Cloud Compare
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter your passcode to access the application
          </Typography>
        </Box>

        <form onSubmit={handleLogin}>
          <TextField
            fullWidth
            type={showPasscode ? 'text' : 'password'}
            label="Passcode"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            required
            disabled={loading}
            autoFocus
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle passcode visibility"
                    onClick={toggleShowPasscode}
                    edge="end"
                  >
                    {showPasscode ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || !passcode.trim()}
            sx={{
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #1976d2 0%, #4c2a85 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #115293 0%, #3a1f64 100%)',
              },
            }}
          >
            {loading ? 'Authenticating...' : 'Access Application'}
          </Button>
        </form>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
          Your Butler for Intelligent Cloud Migration
        </Typography>
        
        <Typography 
          variant="caption" 
          sx={{ 
            mt: 2, 
            display: 'block', 
            color: '#9e9e9e', 
            fontSize: '0.7rem',
            textAlign: 'center' 
          }}
        >
          {VERSION_INFO}
        </Typography>
      </Card>
    </Box>
  );
};

export default Login;